import React, { useState, useRef, useCallback, useLayoutEffect, useEffect } from 'react';
import { BookOpen, ChevronRight, ChevronDown, ChevronLeft, CheckCircle, Play, HelpCircle, Database, Wifi, Box, GripVertical, X, Crosshair } from 'lucide-react';
import type { DemoGraph, DemoDataMode } from '../data/demoGraphs';
import { brandPrimaryBtn } from './brandChrome';
import { JargonText } from './demoJargon';
import { compactWalkthroughSteps } from './demoWalkthrough';

/** Persist "Got it" on the shared UI map so later demos skip the same chrome. */
export const DEMO_UI_MAP_DISMISS_KEY = 'cryptoflow-demo-ui-map-dismissed';

interface DemoGuideProps {
    demo: DemoGraph;
    /** Hide the guide (re-openable via the header "Demo Guide" toggle). */
    onClose?: () => void;
    /** Current data mode */
    dataMode?: DemoDataMode;
    /** Callback when user switches mode */
    onModeChange?: (mode: DemoDataMode) => void;
    /** Whether cached data is available for this demo */
    hasCachedData?: boolean;
    /** Guest sandbox: Live requires Sign In */
    guestMode?: boolean;
    /**
     * Workbench (default) vs Fusion Canvas — same chrome, different UI map and
     * dock target. Fusion hides Mock/Cached/Live (seed is always synthetic).
     */
    surface?: 'workbench' | 'fusion';
    /** Optional Fusion "Where to find" bullets (when surface=fusion). */
    fusionUiMap?: FusionUiMapHints;
}

/** Which Fusion chrome lines to show in the shared Demo Guide UI map. */
export type FusionUiMapHints = {
    palette?: boolean;
    courtSafe?: boolean;
    surfaces?: boolean;
    timeline?: boolean;
    inspector?: boolean;
    ledger?: boolean;
    bankImport?: boolean;
    caseFiles?: boolean;
    build?: boolean;
    journal?: boolean;
    runRail?: boolean;
    radial?: boolean;
    playbook?: boolean;
};

const DEFAULT_FUSION_UI_MAP: FusionUiMapHints = {
    palette: true,
    courtSafe: true,
    surfaces: true,
    timeline: true,
    inspector: true,
    ledger: true,
    bankImport: true,
    caseFiles: true,
    build: true,
    journal: true,
    runRail: true,
    radial: true,
    playbook: true,
};

export const DemoGuide: React.FC<DemoGuideProps> = ({
    demo,
    onClose,
    dataMode = 'mock',
    onModeChange,
    hasCachedData = false,
    guestMode = false,
    surface = 'workbench',
    fusionUiMap,
}) => {
    const isFusion = surface === 'fusion';
    const fusionMap: FusionUiMapHints = fusionUiMap
        ? { ...Object.fromEntries((Object.keys(DEFAULT_FUSION_UI_MAP) as (keyof FusionUiMapHints)[]).map((k) => [k, false])), ...fusionUiMap }
        : DEFAULT_FUSION_UI_MAP;
    const walkthrough = compactWalkthroughSteps(demo.howTo);
    const [showFullGuide, setShowFullGuide] = useState(false);
    const steps = showFullGuide ? walkthrough.fullSteps : walkthrough.steps;
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
    const [expanded, setExpanded] = useState(true);
    const [uiMapDismissed, setUiMapDismissed] = useState(
        () => typeof window !== 'undefined' && localStorage.getItem(DEMO_UI_MAP_DISMISS_KEY) === '1',
    );

    const dismissUiMap = () => {
        try { localStorage.setItem(DEMO_UI_MAP_DISMISS_KEY, '1'); } catch { /* private mode */ }
        setUiMapDismissed(true);
    };

    const showUiMap = () => {
        try { localStorage.removeItem(DEMO_UI_MAP_DISMISS_KEY); } catch { /* private mode */ }
        setUiMapDismissed(false);
    };

    // Reset walkthrough when the active demo changes.
    useEffect(() => {
        setCurrentStep(0);
        setCompletedSteps(new Set());
        setExpanded(true);
        setShowFullGuide(false);
    }, [demo.id]);

    // Clamp step index when switching focused ↔ full guide lengths.
    useEffect(() => {
        setCurrentStep((prev) => (steps.length === 0 ? 0 : Math.min(prev, steps.length - 1)));
        setCompletedSteps(new Set());
    }, [showFullGuide, demo.id, steps.length]);

    // ── Free-drag: null = default anchored position; once dragged, absolute x/y. ──
    const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
    const dragging = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const panelRef = useRef<HTMLDivElement>(null);

    const onDragMove = useCallback((e: MouseEvent) => {
        if (!dragging.current) return;
        const w = panelRef.current?.offsetWidth ?? 420;
        const h = panelRef.current?.offsetHeight ?? 300;
        const x = Math.max(0, Math.min(e.clientX - dragOffset.current.x, window.innerWidth - w));
        const y = Math.max(0, Math.min(e.clientY - dragOffset.current.y, window.innerHeight - h));
        setPos({ x, y });
    }, []);

    const onDragEnd = useCallback(() => {
        dragging.current = false;
        window.removeEventListener('mousemove', onDragMove);
        window.removeEventListener('mouseup', onDragEnd);
        document.body.style.userSelect = '';
    }, [onDragMove]);

    const onDragStart = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return; // left-click only
        const rect = panelRef.current?.getBoundingClientRect();
        if (!rect) return;
        dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        dragging.current = true;
        document.body.style.userSelect = 'none';
        window.addEventListener('mousemove', onDragMove);
        window.addEventListener('mouseup', onDragEnd);
    }, [onDragMove, onDragEnd]);

    // ── Free-resize via edge/corner handles (header stays the move handle). ──
    const [size, setSize] = useState<{ w: number; h: number } | null>(null);
    const resizing = useRef<null | { edges: string; startX: number; startY: number; rect: DOMRect }>(null);
    const MIN_W = 260;
    const MIN_H = 150;

    const onResizeMove = useCallback((e: MouseEvent) => {
        const r = resizing.current;
        if (!r) return;
        const dx = e.clientX - r.startX;
        const dy = e.clientY - r.startY;
        const right = r.rect.right, bottom = r.rect.bottom;
        let left = r.rect.left, top = r.rect.top, width = r.rect.width, height = r.rect.height;
        // East/South grow from the delta; West/North move the origin (anchored to the opposite edge).
        if (r.edges.includes('e')) width = r.rect.width + dx;
        if (r.edges.includes('s')) height = r.rect.height + dy;
        if (r.edges.includes('w')) { left = Math.max(0, Math.min(r.rect.left + dx, right - MIN_W)); width = right - left; }
        if (r.edges.includes('n')) { top = Math.max(0, Math.min(r.rect.top + dy, bottom - MIN_H)); height = bottom - top; }
        width = Math.min(Math.max(MIN_W, width), window.innerWidth - left);
        height = Math.min(Math.max(MIN_H, height), window.innerHeight - top);
        setPos({ x: left, y: top });
        setSize({ w: width, h: height });
    }, []);

    const onResizeEnd = useCallback(() => {
        resizing.current = null;
        window.removeEventListener('mousemove', onResizeMove);
        window.removeEventListener('mouseup', onResizeEnd);
        document.body.style.userSelect = '';
    }, [onResizeMove]);

    // On open, dock the guide just to the LEFT of the inspector rail (Workbench
    // Node Inspector or Fusion aside — whichever is on this surface).
    useLayoutEffect(() => {
        const w = panelRef.current?.offsetWidth ?? 420;
        const gap = 20;
        const top = 112;
        const inspSel = isFusion
            ? '[data-testid="fusion-inspector-aside"]'
            : '[data-testid="wb-node-inspector"]';
        const insp = document.querySelector(inspSel) as HTMLElement | null;
        const inspLeft = insp ? insp.getBoundingClientRect().left : window.innerWidth;
        const left = Math.max(8, inspLeft - gap - w);
        setPos({ x: left, y: top });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFusion]);

    const startResize = useCallback((edges: string) => (e: React.MouseEvent) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        const rect = panelRef.current?.getBoundingClientRect();
        if (!rect) return;
        // Pin to explicit top/left + size so all edges resize predictably.
        setPos({ x: rect.left, y: rect.top });
        setSize({ w: rect.width, h: rect.height });
        resizing.current = { edges, startX: e.clientX, startY: e.clientY, rect };
        document.body.style.userSelect = 'none';
        window.addEventListener('mousemove', onResizeMove);
        window.addEventListener('mouseup', onResizeEnd);
    }, [onResizeMove, onResizeEnd]);

    const markStepComplete = (index: number) => {
        setCompletedSteps(prev => {
            if (prev.has(index)) return prev;
            const next = new Set(prev);
            next.add(index);
            return next;
        });
    };

    const goNext = () => {
        if (steps.length === 0) return;
        markStepComplete(currentStep);
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const goPrev = () => {
        if (currentStep > 0) setCurrentStep(currentStep - 1);
    };

    const jumpToStep = (index: number) => {
        setCurrentStep(index);
        setExpanded(true);
    };

    const progressPct = steps.length > 0
        ? Math.min(100, Math.round((completedSteps.size / steps.length) * 100))
        : 0;
    // Bar also advances slightly for the active step so progress feels responsive before mark-complete.
    const barPct = steps.length > 0
        ? Math.min(100, Math.max(progressPct, Math.round(((currentStep + 1) / steps.length) * 100 * 0.2)))
        : 0;
    const allDone = steps.length > 0 && completedSteps.size === steps.length;
    const isLastStep = currentStep >= steps.length - 1;

    return (
        <div
            ref={panelRef}
            className={`fixed z-40 bg-card border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden ${size ? '' : 'w-[420px] max-w-[calc(100vw-2rem)] max-h-[72vh]'} ${pos ? '' : 'right-[440px] top-28 animate-in slide-in-from-top-5'}`}
            style={{
                ...(pos ? { left: pos.x, top: pos.y } : {}),
                ...(size ? { width: size.w, height: size.h } : {}),
            }}
        >
            {/* Resize handles — all four edges + all four corners.
                (Corners sit above the edges/header via z-20 so they win the grab.) */}
            <div onMouseDown={startResize('w')} className="absolute top-0 left-0 h-full w-1.5 cursor-ew-resize z-10" />
            <div onMouseDown={startResize('e')} className="absolute top-0 right-0 h-full w-1.5 cursor-ew-resize z-10" />
            <div onMouseDown={startResize('s')} className="absolute bottom-0 left-0 w-full h-1.5 cursor-ns-resize z-10" />
            <div onMouseDown={startResize('n')} className="absolute top-0 left-0 w-full h-1.5 cursor-ns-resize z-10" />
            <div onMouseDown={startResize('sw')} className="absolute bottom-0 left-0 w-3 h-3 cursor-nesw-resize z-20" />
            <div onMouseDown={startResize('se')} className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize z-20" />
            <div onMouseDown={startResize('nw')} className="absolute top-0 left-0 w-3 h-3 cursor-nwse-resize z-20" />
            <div onMouseDown={startResize('ne')} className="absolute top-0 right-0 w-3 h-3 cursor-nesw-resize z-20" />
            {/* Header — brand ink + gold accent bar (drag handle) */}
            <div
                onMouseDown={onDragStart}
                className="bg-brand-ink px-4 py-3 flex items-center justify-between cursor-move select-none border-b-4 border-b-primary"
                title="Drag to move"
            >
                <div className="flex items-center gap-2 text-white min-w-0">
                    <GripVertical className="w-4 h-4 text-white/50 shrink-0" />
                    <BookOpen className="w-5 h-5 shrink-0 text-primary" />
                    <div className="min-w-0">
                        <h3 className="font-semibold text-base leading-tight text-white">Demo Guide</h3>
                        <p className="text-xs text-white/70 truncate">{demo.title}</p>
                    </div>
                </div>
                {onClose && (
                    <button
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={onClose}
                        className="p-1 hover:bg-white/10 rounded text-white/60 hover:text-white transition-colors shrink-0"
                        title="Hide Demo Guide (reopen from the toolbar)"
                        aria-label="Hide Demo Guide"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Progress Bar */}
            {steps.length > 0 && (
                <div className="px-4 pt-3 pb-1">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                        <span>Step {currentStep + 1} of {steps.length}</span>
                        <span>{progressPct}% complete</span>
                    </div>
                    {walkthrough.truncated && (
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-[10px] text-muted-foreground/90">
                                Focused walkthrough ({walkthrough.steps.length} key steps)
                            </span>
                            <button
                                type="button"
                                onClick={() => setShowFullGuide((v) => !v)}
                                className="text-[10px] font-medium text-primary hover:underline shrink-0"
                            >
                                {showFullGuide
                                    ? `Show focused (${walkthrough.steps.length})`
                                    : `Show all ${walkthrough.fullSteps.length} steps`}
                            </button>
                        </div>
                    )}
                    <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div
                            className="h-full bg-primary rounded-full transition-all duration-500"
                            style={{ width: `${barPct}%` }}
                        />
                    </div>
                    {/* Step dots — click to jump */}
                    <div className="flex items-center gap-1 mt-2" role="tablist" aria-label="Demo steps">
                        {steps.map((_, index) => {
                            const done = completedSteps.has(index);
                            const active = index === currentStep;
                            return (
                                <button
                                    key={index}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    aria-label={`Step ${index + 1}${done ? ' (done)' : ''}`}
                                    onClick={() => jumpToStep(index)}
                                    className={`h-1.5 flex-1 rounded-full transition-colors ${
                                        active ? 'bg-primary' : done ? 'bg-primary/50' : 'bg-border hover:bg-muted-foreground/30'
                                    }`}
                                />
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Content — extra top padding so first-card jargon tips have room */}
            <div className="flex-1 overflow-y-auto px-4 pt-5 pb-3 space-y-3">
                {/* Description */}
                {demo.description && (
                    <div className="text-sm text-muted-foreground leading-relaxed bg-brand-cream/40 dark:bg-primary/5 rounded-lg p-3 pt-4 border border-primary/20">
                        <div className="flex items-center gap-1.5 text-foreground font-medium mb-2">
                            <Play className="w-3 h-3 text-primary" />
                            About this scenario
                        </div>
                        <JargonText text={demo.description} />
                        <p className="mt-2 text-[10px] text-muted-foreground/80 leading-snug">
                            Hover any <span className="border-b border-dotted border-primary/70 text-foreground">dotted term</span> for a short explainer.
                            Terms naming a part of the screen are clickable — clicking points them out.
                        </p>
                    </div>
                )}

                {uiMapDismissed ? (
                    <button
                        type="button"
                        onClick={showUiMap}
                        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <HelpCircle className="w-3 h-3 text-primary shrink-0" aria-hidden />
                        Show UI map
                    </button>
                ) : (
                    <div className="text-xs text-foreground leading-relaxed bg-muted/50 rounded-lg p-3 border-l-2 border-primary/70">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                            <div className="flex items-center gap-1.5 text-foreground font-medium">
                                <HelpCircle className="w-3 h-3 text-primary" />
                                Where to find features
                            </div>
                            <button
                                type="button"
                                onClick={dismissUiMap}
                                className="shrink-0 text-[11px] font-medium text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded hover:bg-muted transition-colors"
                            >
                                Got it
                            </button>
                        </div>
                        <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground">
                            {isFusion ? (
                                <>
                                    {fusionMap.palette && <li><JargonText text="Palette — left rail: Evidence In, Operators, Algorithms, Playbooks" /></li>}
                                    {fusionMap.courtSafe && <li><JargonText text="Court-safe — top bar toggle; dims scored links, keeps UTR / hash / registry joins" /></li>}
                                    {fusionMap.surfaces && <li><JargonText text="Surface filters — open Legend; click crypto / bank / social / company / network to show or hide" /></li>}
                                    {fusionMap.timeline && <li><JargonText text="Ingestion Timeline — right-side dot + label; click to expand Play stages at the bottom" /></li>}
                                    {fusionMap.inspector && <li><JargonText text="Fusion Inspector — right rail; opens when you click a node" /></li>}
                                    {fusionMap.ledger && <li><JargonText text="Court-safe Chain — right-rail tab with the deterministic evidence table" /></li>}
                                    {fusionMap.bankImport && <li><JargonText text="Bank import — right-rail tab for StatementFlow upload (config-gated)" /></li>}
                                    {fusionMap.caseFiles && <li><JargonText text="Case files — right-rail dossier: holder, account, txs, counterparties, Dr/Cr; promote demo → real case" /></li>}
                                    <li><JargonText text="For a live FIR: on Build pick Create a new case (or New case in the title bar) — demos stay on the sandbox" /></li>
                                    <li><JargonText text="Reopen an existing case: Cases opens it on its file page — press Open Fusion Canvas there (or the sparkle on the case card); Open case in the Fusion sidebar goes straight to the canvas" /></li>
                                    {fusionMap.build && <li><JargonText text="Build tab — author evidence cards and press Run" /></li>}
                                    {fusionMap.journal && <li><JargonText text="Journal tab — build history; Replay all / Up to here / From here scrub the canvas" /></li>}
                                    {fusionMap.runRail && <li><JargonText text="Run rail — strip under the canvas; job chips from operators / cards / Autopilot (click to focus)" /></li>}
                                    {fusionMap.radial && <li><JargonText text="Radial menu — right-click a node for operators valid on that type" /></li>}
                                    {fusionMap.playbook && <li><JargonText text="Autopilot — Palette → Playbooks; posts jobs on the Run rail as it walks the case" /></li>}
                                </>
                            ) : (
                                <>
                                    {(!demo.navHints || demo.navHints.transactions) && <li><JargonText text="Transfers tab — sample txs in Mock; Load only in Live mode" /></li>}
                                    {(!demo.navHints || demo.navHints.profile || demo.navHints.ofac || demo.navHints.bridges || demo.navHints.evm || demo.navHints.crossChain || demo.navHints.privacy === 'intelligence-tab') && <li><JargonText text={`Intel tab — Sanctions, Profile, Exposure, Bridges${demo.navHints?.crossChain === 'intelligence-tab' ? ', Cross-Chain Activity' : ''}${demo.navHints?.evm ? ', DEX Swaps' : ''}${demo.navHints?.privacy === 'intelligence-tab' ? ', Privacy Deep Check, Ring Elimination' : ''}`} /></li>}
                                    {(demo.navHints?.crossChain === 'triage-ribbon' || demo.navHints?.privacy === 'triage-ribbon' || demo.navHints?.chainHopping === 'triage-ribbon') && <li><JargonText text="Node Inspector identity card — risk, sanctions, entity, nearest exchange, Bridge, and Cross-chain chips appear when available" /></li>}
                                    {demo.navHints?.privacy === 'canvas' && <li><JargonText text="Canvas edges — purple-dashed = mixer deposit, purple-dotted = withdrawal, blue-dashed = CoinJoin, red border = sanctioned mixer" /></li>}
                                    {(!demo.navHints || demo.navHints.analytics || demo.navHints.chainHopping === 'analysis-tab') && <li><JargonText text={`Analysis tab (G / I) — Investigation, AI Summary, Pattern Scan${demo.navHints?.chainHopping === 'analysis-tab' ? ', Chain-Hopping Sequence' : ''}`} /></li>}
                                    {demo.navHints?.undoRedo && <li><JargonText text="Undo/Redo — Ctrl+Z / Ctrl+Shift+Z in header toolbar" /></li>}
                                    {demo.navHints?.findLinks && <li><JargonText text="Find Links — discover paths between addresses (header toolbar)" /></li>}
                                    {(!demo.navHints || demo.navHints.actions) && <li><JargonText text="Actions tab — Flag, Export, Legal Notice" /></li>}
                                    <li><JargonText text="Fund Flow Playback — below the canvas, replay transfers for court / training walkthroughs" /></li>
                                    <li><JargonText text="Case wallets — upper-right of the canvas when the Workbench was opened from a case; click to select, no extra fetch" /></li>
                                    <li><JargonText text="Promote to case — Analysis tab, Targeted Node Intelligence, after a run on a Workbench opened from a case" /></li>
                                    <li><JargonText text="Rearrange as — Smart / Flow / Organic / Radial (bottom canvas strip, beside Recompute)" /></li>
                                </>
                            )}
                        </ul>
                        <p className="mt-2 flex items-center gap-1 text-[10px] text-muted-foreground">
                            <Crosshair className="w-3 h-3 text-primary shrink-0" aria-hidden />
                            Click an underlined feature name to point it out on screen.
                        </p>
                    </div>
                )}

                {/* ═══════ DATA MODE TOGGLE (Workbench only — Fusion seed is always synthetic) ═══════ */}
                {!isFusion && demo.supportedModes && demo.supportedModes.length > 1 && onModeChange && (
                    <div className="bg-secondary/50 p-3 rounded-lg border border-border">
                        <div className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                            <Database className="w-3 h-3 text-muted-foreground" />
                            Data Source
                        </div>
                        <div className="flex gap-1.5">
                            {demo.supportedModes.includes('mock') && (
                                <button
                                    onClick={() => onModeChange('mock')}
                                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-medium border transition-all ${
                                        dataMode === 'mock'
                                            ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                                            : 'bg-secondary border-border text-foreground hover:bg-muted'
                                    }`}
                                    title="Clean pre-built pattern illustration"
                                >
                                    <Box className="w-3 h-3" />
                                    Mock
                                </button>
                            )}
                            {demo.supportedModes.includes('cached') && (
                                <button
                                    onClick={() => onModeChange('cached')}
                                    disabled={!hasCachedData}
                                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-medium border transition-all ${
                                        dataMode === 'cached'
                                            ? 'bg-brand-ink text-primary border-brand-ink shadow-sm dark:bg-primary dark:text-primary-foreground dark:border-primary'
                                            : hasCachedData
                                                ? 'bg-secondary border-border text-foreground hover:bg-muted'
                                                : 'bg-secondary border-border text-muted-foreground cursor-not-allowed opacity-50'
                                    }`}
                                    title={hasCachedData ? 'Real blockchain data (pre-fetched, loads instantly)' : 'No cached data — use admin to populate'}
                                >
                                    <Database className="w-3 h-3" />
                                    Cached
                                </button>
                            )}
                            {demo.supportedModes.includes('live') && (
                                <button
                                    onClick={() => onModeChange('live')}
                                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-medium border transition-all ${
                                        dataMode === 'live'
                                            ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                                            : guestMode
                                                ? 'bg-secondary border-border text-muted-foreground hover:bg-muted'
                                                : 'bg-secondary border-border text-foreground hover:bg-primary/10'
                                    }`}
                                    title={guestMode ? 'Sign in to use Live chain data' : 'Hits live blockchain APIs (slower, real-time data)'}
                                    data-testid="demo-guide-mode-live"
                                >
                                    <Wifi className="w-3 h-3" />
                                    Live{guestMode ? ' (Sign in)' : ''}
                                </button>
                            )}
                        </div>
                        <p className="mt-2 text-[11px] text-muted-foreground">
                            {dataMode === 'mock' && 'Pre-built pattern illustration with synthetic addresses.'}
                            {dataMode === 'cached' && 'Real blockchain data (pre-fetched). Shows how real investigations look.'}
                            {dataMode === 'live' && 'Live API calls to blockchain explorers. May be slower.'}
                        </p>
                        {dataMode !== 'mock' && (demo.realAddress || demo.address) && (
                            <div className="mt-1.5 text-[11px] bg-secondary rounded px-2 py-1.5 border border-border leading-relaxed">
                                <span className="text-muted-foreground">Address: </span>
                                <span className="font-mono text-foreground">{(demo.realAddress || demo.address || '').slice(0, 18)}...</span>
                                <span className="text-muted-foreground"> on </span>
                                <span className="font-semibold text-foreground">{demo.realChain || demo.chain}</span>
                                {demo.realSource && (
                                    <>
                                        <br />
                                        <span className="text-muted-foreground">Source: {demo.realSource}</span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Current step (wizard) */}
                {steps.length > 0 && (
                    <div>
                        <button
                            onClick={() => setExpanded(!expanded)}
                            className="flex items-center gap-1.5 text-sm font-semibold text-foreground mb-2 hover:text-primary transition-colors"
                        >
                            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            Step-by-Step Instructions
                        </button>

                        {expanded && (
                            <div className="space-y-3">
                                <div
                                    className={`rounded-lg border p-3.5 transition-colors ${
                                        completedSteps.has(currentStep)
                                            ? 'bg-primary/10 border-primary/40 dark:bg-primary/15 dark:border-primary/50'
                                            : 'bg-card border-primary/40 shadow-sm'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-ink text-primary text-xs font-bold shrink-0">
                                            {currentStep + 1}
                                        </span>
                                        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                            {completedSteps.has(currentStep) ? 'Completed' : 'Do this on the canvas'}
                                        </span>
                                        {completedSteps.has(currentStep) && (
                                            <CheckCircle className="w-3.5 h-3.5 text-primary ml-auto" />
                                        )}
                                    </div>
                                    <p className="text-sm text-foreground leading-relaxed">
                                        <JargonText text={steps[currentStep]} />
                                    </p>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={goPrev}
                                        disabled={currentStep === 0}
                                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-border bg-secondary text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ChevronLeft className="w-3.5 h-3.5" />
                                        Back
                                    </button>
                                    <button
                                        type="button"
                                        onClick={goNext}
                                        className={`flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded-md ${brandPrimaryBtn}`}
                                    >
                                        {isLastStep && !allDone ? (
                                            <>
                                                <CheckCircle className="w-3.5 h-3.5" />
                                                Mark complete
                                            </>
                                        ) : isLastStep && allDone ? (
                                            <>
                                                <CheckCircle className="w-3.5 h-3.5" />
                                                All done
                                            </>
                                        ) : (
                                            <>
                                                Next
                                                <ChevronRight className="w-3.5 h-3.5" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="border-t border-border px-4 py-2 bg-secondary/20 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                    Hover to explain · click to locate · then Next
                </span>
                {allDone && (
                    <span className="text-xs text-primary font-semibold flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        All steps done!
                    </span>
                )}
            </div>
        </div>
    );
};
